import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buscarProductos } from '../../utils/buscarProductos';
import {
    ClipboardList, Plus, Search, TrendingUp, TrendingDown,
    RefreshCw, X, AlertCircle, Package
} from 'lucide-react';

const BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario`;

const TIPOS = [
    { key: 'entrada',    label: 'Entrada',     desc: 'Suma unidades (rotura de bulto, devolución, etc.)',  color: 'text-exito-400', bg: 'bg-exito-500/10 border-exito-500/30' },
    { key: 'salida',     label: 'Salida',      desc: 'Resta unidades (pérdida, rotura, muestra, etc.)',    color: 'text-error-400',     bg: 'bg-error-500/10 border-error-500/30' },
    { key: 'correccion', label: 'Corrección',  desc: 'Establece el stock exacto (inventario físico)',       color: 'text-brand-800',    bg: 'bg-brand-500/10 border-brand-500/30' },
];

const TIPO_META = {
    entrada:    { icon: <TrendingUp size={14} />,  color: 'text-exito-400', badge: 'bg-exito-500/10 text-exito-400 border-exito-500/20' },
    salida:     { icon: <TrendingDown size={14} />, color: 'text-error-400',    badge: 'bg-error-500/10 text-error-400 border-error-500/20' },
    correccion: { icon: <RefreshCw size={14} />,   color: 'text-brand-800',   badge: 'bg-brand-500/10 text-brand-800 border-brand-500/20' },
};

const EMPTY = { producto: '', tipo: 'entrada', cantidad: '', stock_nuevo_manual: '', motivo: '' };

export default function AjusteInventario() {
    const [ajustes, setAjustes]       = useState([]);
    const [productos, setProductos]   = useState([]);
    const [modal, setModal]           = useState(false);
    const [form, setForm]             = useState(EMPTY);
    const [prodSearch, setProdSearch] = useState('');
    const [prodSugg, setProdSugg]     = useState([]);
    const [prodSel, setProdSel]       = useState(null);
    const [saving, setSaving]         = useState(false);
    const [error, setError]           = useState(null);
    const [search, setSearch]         = useState('');
    const [loading, setLoading]       = useState(false);


    const fetchAll = async () => {
        setLoading(true);
        try {
            const [ra, rp] = await Promise.all([
                axios.get(`${BASE}/ajustes-inventario/`),
                axios.get(`${BASE}/productos/`),
            ]);
            setAjustes(ra.data);
            setProductos(rp.data.filter(p => p.activo));
        } catch {
            setError('Error al cargar datos.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const handleProdSearch = (val) => {
        setProdSearch(val);
        setProdSel(null);
        setForm(f => ({ ...f, producto: '' }));
        if (val.length >= 2) {
            setProdSugg(buscarProductos(productos, val).slice(0, 6));
        } else {
            setProdSugg([]);
        }
    };

    const selProd = (p) => {
        setProdSel(p);
        setProdSearch(p.nombre);
        setForm(f => ({ ...f, producto: p.id }));
        setProdSugg([]);
    };

    const openModal = () => {
        setForm(EMPTY);
        setProdSearch('');
        setProdSel(null);
        setProdSugg([]);
        setError(null);
        setModal(true);
    };

    const closeModal = () => { setModal(false); setError(null); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.producto) { setError('Seleccioná un producto.'); return; }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                producto: form.producto,
                tipo:     form.tipo,
                motivo:   form.motivo,
                ...(form.tipo === 'correccion'
                    ? { stock_nuevo_manual: parseInt(form.stock_nuevo_manual) }
                    : { cantidad: parseInt(form.cantidad) }),
            };
            await axios.post(`${BASE}/ajustes-inventario/`, payload);
            closeModal();
            fetchAll();
        } catch (err) {
            const d = err.response?.data;
            const msg = d?.cantidad?.[0] || d?.stock_nuevo_manual?.[0] || d?.motivo?.[0]
                || d?.non_field_errors?.[0] || d?.detail || 'Error al registrar el ajuste.';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const ajustesFiltrados = ajustes.filter(a =>
        a.producto_nombre?.toLowerCase().includes(search.toLowerCase()) ||
        a.motivo?.toLowerCase().includes(search.toLowerCase())
    );

    const fmtDiff = (a) => {
        const diff = a.cantidad;
        if (a.tipo === 'correccion') {
            if (diff === 0) return <span className="text-slate-400">sin cambio</span>;
            return diff > 0
                ? <span className="text-exito-400">+{diff}</span>
                : <span className="text-error-400">{diff}</span>;
        }
        return a.tipo === 'entrada'
            ? <span className="text-exito-400">+{diff}</span>
            : <span className="text-error-400">-{Math.abs(diff)}</span>;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-slate-900/60 backdrop-blur-sm p-6 rounded-2xl border border-slate-200 dark:border-white/10 gap-4">
                <div className="flex items-center gap-4">
                    <div className="bg-brand-500/20 p-3 rounded-xl border border-brand-500/30">
                        <ClipboardList size={24} className="text-brand-800" />
                    </div>
                    <div>
                        <h2 className="titulo-modal">Ajuste de Inventario</h2>
                        <p className="text-sm text-slate-500 dark:text-gray-400">Registrá entradas, salidas y correcciones de stock con trazabilidad</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search size={15} className="icono-campo" />
                        <input
                            type="text" placeholder="Buscar por producto o motivo..."
                            value={search} onChange={e => setSearch(e.target.value)}
                            className="pl-9 pr-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 w-64"
                        />
                    </div>
                    <button
                        onClick={openModal}
                        className="bg-brand-600 hover:bg-brand-500 text-negro-950 px-5 py-2.5 rounded-xl shadow-lg shadow-brand-500/30 font-semibold flex items-center gap-2 transition-all"
                    >
                        <Plus size={18} /> Nuevo Ajuste
                    </button>
                </div>
            </div>

            {/* Tabla historial */}
            <div className="bg-white dark:bg-slate-900/60 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="tabla-cabecera">
                                <th className="px-6 py-4 font-medium">Producto</th>
                                <th className="px-6 py-4 font-medium text-center">Tipo</th>
                                <th className="px-6 py-4 font-medium text-center">Antes</th>
                                <th className="px-6 py-4 font-medium text-center">Diferencia</th>
                                <th className="px-6 py-4 font-medium text-center">Después</th>
                                <th className="px-6 py-4 font-medium">Motivo</th>
                                <th className="px-6 py-4 font-medium">Usuario</th>
                                <th className="px-6 py-4 font-medium">Fecha</th>
                            </tr>
                        </thead>
                        <tbody className="tabla-filas">
                            {loading && (
                                <tr><td colSpan="8" className="px-6 py-10 text-center text-slate-400 dark:text-gray-500">Cargando...</td></tr>
                            )}
                            {!loading && ajustesFiltrados.map(a => {
                                const meta = TIPO_META[a.tipo] || TIPO_META.correccion;
                                return (
                                    <tr key={a.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-semibold text-slate-900 dark:text-white">{a.producto_nombre}</div>
                                            <div className="text-xs text-slate-400 dark:text-gray-500 font-mono">{a.producto_codigo}</div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${meta.badge}`}>
                                                {meta.icon}{a.tipo_display}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center font-mono text-slate-500 dark:text-gray-400">{a.stock_anterior}</td>
                                        <td className="px-6 py-4 text-center font-bold font-mono">{fmtDiff(a)}</td>
                                        <td className="px-6 py-4 text-center font-bold font-mono text-slate-900 dark:text-white">{a.stock_nuevo}</td>
                                        <td className="px-6 py-4 text-slate-600 dark:text-gray-300 max-w-xs truncate">{a.motivo}</td>
                                        <td className="px-6 py-4 text-slate-400 dark:text-gray-500 text-xs">{a.usuario_nombre || '—'}</td>
                                        <td className="px-6 py-4 text-slate-400 dark:text-gray-500 text-xs whitespace-nowrap">
                                            {new Date(a.fecha).toLocaleString('es-PY', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                    </tr>
                                );
                            })}
                            {!loading && ajustesFiltrados.length === 0 && (
                                <tr>
                                    <td colSpan="8" className="px-6 py-16 text-center text-slate-400 dark:text-gray-500">
                                        <Package size={40} className="mx-auto mb-3 opacity-20" />
                                        <p>No hay ajustes registrados todavía.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Nuevo Ajuste */}
            {modal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/20 rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-slate-200 dark:border-white/10 flex justify-between items-center bg-slate-50 dark:bg-gray-800/40">
                            <h3 className="titulo-modal">
                                <ClipboardList className="text-brand-800" size={22} />
                                Nuevo Ajuste de Inventario
                            </h3>
                            <button onClick={closeModal} className="text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                                <X size={22} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            {error && (
                                <div className="bg-error-500/10 border border-error-500/20 text-error-400 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                                    <AlertCircle size={16} className="shrink-0" />{error}
                                </div>
                            )}

                            {/* Buscador de producto */}
                            <div>
                                <label htmlFor="ajuste-producto" className="etiqueta-campo">Producto *</label>
                                <div className="relative">
                                    <Search size={15} className="icono-campo" />
                                    <input id="ajuste-producto"
                                        type="text" placeholder="Buscar por nombre o código..."
                                        value={prodSearch} onChange={e => handleProdSearch(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                                    />
                                    {prodSugg.length > 0 && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/20 rounded-xl shadow-2xl z-10 overflow-hidden">
                                            {prodSugg.map(p => (
                                                <button key={p.id} type="button" onClick={() => selProd(p)}
                                                    className="w-full text-left px-4 py-2.5 text-sm text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-colors border-b border-slate-100 dark:border-white/5 last:border-0">
                                                    <div className="font-medium">{p.nombre}</div>
                                                    <div className="text-xs text-slate-400 dark:text-gray-500 flex gap-3">
                                                        <span className="font-mono">{p.codigo_barras}</span>
                                                        <span>Stock actual: <strong>{p.stock_actual}</strong></span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {prodSel && (
                                    <div className="mt-2 flex items-center justify-between bg-brand-500/10 border border-brand-500/30 px-3 py-2 rounded-lg">
                                        <div>
                                            <span className="text-sm text-brand-800 font-semibold">{prodSel.nombre}</span>
                                            <span className="text-xs text-slate-400 dark:text-gray-500 ml-2">Stock actual: <strong className="text-white">{prodSel.stock_actual}</strong></span>
                                        </div>
                                        <button type="button" onClick={() => { setProdSel(null); setProdSearch(''); setForm(f => ({ ...f, producto: '' })); }}
                                            className="text-slate-400 hover:text-error-400"><X size={14} /></button>
                                    </div>
                                )}
                            </div>

                            {/* Tipo de ajuste */}
                            <div>
                                <span id="ajuste-tipo-etiqueta" className="etiqueta-campo">Tipo de ajuste *</span>
                                <div className="space-y-2" role="radiogroup" aria-labelledby="ajuste-tipo-etiqueta">
                                    {TIPOS.map(t => (
                                        <button key={t.key} type="button" role="radio" aria-checked={form.tipo === t.key}
                                            onClick={() => setForm(f => ({ ...f, tipo: t.key }))}
                                            className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                                                form.tipo === t.key ? t.bg : 'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10'
                                            }`}>
                                            <span className={`font-bold text-sm ${form.tipo === t.key ? t.color : 'text-slate-500 dark:text-gray-400'}`}>{t.label}</span>
                                            <span className="text-xs text-slate-400 dark:text-gray-500 ml-2">{t.desc}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Cantidad o stock final según tipo */}
                            {form.tipo === 'correccion' ? (
                                <div>
                                    <label htmlFor="ajuste-stock-final" className="etiqueta-campo">
                                        Stock final correcto *
                                        {prodSel && <span className="ml-2 font-normal normal-case text-slate-400">(actual: {prodSel.stock_actual})</span>}
                                    </label>
                                    <input id="ajuste-stock-final" required type="number" min="0"
                                        className="campo"
                                        placeholder="Ingresá el stock real contado"
                                        value={form.stock_nuevo_manual}
                                        onChange={e => setForm(f => ({ ...f, stock_nuevo_manual: e.target.value }))} />
                                </div>
                            ) : (
                                <div>
                                    <label htmlFor="ajuste-cantidad" className="etiqueta-campo">Cantidad *</label>
                                    <input id="ajuste-cantidad" required type="number" min="1"
                                        className="campo"
                                        placeholder="Unidades a ajustar"
                                        value={form.cantidad}
                                        onChange={e => setForm(f => ({ ...f, cantidad: e.target.value }))} />
                                </div>
                            )}

                            {/* Motivo */}
                            <div>
                                <label htmlFor="ajuste-motivo" className="etiqueta-campo">Motivo *</label>
                                <input id="ajuste-motivo" required type="text" maxLength={300}
                                    className="campo"
                                    placeholder="Ej: Inventario físico, rotura, devolución de cliente..."
                                    value={form.motivo}
                                    onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))} />
                            </div>

                            <div className="pt-2 flex justify-end gap-3 border-t border-slate-200 dark:border-white/10">
                                <button type="button" onClick={closeModal}
                                    className="px-5 py-2.5 rounded-xl text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
                                    Cancelar
                                </button>
                                <button type="submit" disabled={saving}
                                    className="bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-negro-950 px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-brand-500/20 transition-all">
                                    {saving ? 'Guardando...' : 'Registrar Ajuste'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
